import {
  AppVariation,
  type ClientConfig,
} from "src/client-config/getClientConfig.ts";
import { routes } from "src/constants/routes.constants.ts";
import { Navigate, redirect } from "react-router-dom";
import { getModules } from "src/system/getModules.ts";
import { getModuleRouteObjects } from "src/system/getModuleRouteObjects.tsx";

const loginPageB2BRoute = {
  path: routes.root.root,
  lazy: () => import("src/auth/pages/LoginPageB2B/LoginPageB2B.tsx"),
};

const loginPageRoute = {
  path: routes.root.root,
  lazy: () => import("src/auth/pages/LoginPage/LoginPage.tsx"),
};

const notFoundRoute = {
  path: "*",
  lazy: () => import("src/admin/not-found/not-found.tsx"),
};

const billRoute = {
  path: routes.admin.bill.root,
  lazy: () => import("src/admin/bill/bill.tsx"),
  children: [
    {
      index: true,
      lazy: () => import("src/admin/bill/balance/Balance.tsx"),
    },
    {
      path: routes.admin.bill.account_movements.root,
      lazy: () =>
        import("src/admin/bill/account-movements/AccountMovements.tsx"),
    },
  ],
};

const dashboardRoute = {
  path: routes.admin.dashboard.root,
  lazy: () => import("src/admin/dashboard/dashboard.tsx"),
};

const basicConfigurationRoute = {
  index: true,
  lazy: () => import("src/admin/administration/tabs/BasicConfigurationTab.tsx"),
};

const systemTabRoute = {
  path: routes.admin.administration.system.root,
  lazy: () => import("src/admin/administration/tabs/SystemTab/SystemTab.tsx"),
};

const debugRoute = {
  path: routes.admin.administration.debug.root,
  lazy: () => import("src/admin/administration/tabs/DebugTab.tsx"),
};

const importProductsRoute = {
  lazy: () => import("src/admin/import/import.tsx"),
  path: routes.admin.import.root,
  children: [
    {
      index: true,
      lazy: () => import("src/admin/import/form/import-products.route.tsx"),
    },
  ],
};

const accountingExportRoute = {
  lazy: () => import("src/admin/accounting-export/export.tsx"),
  path: routes.admin.accountingExport.root,
  children: [
    {
      index: true,
      lazy: () =>
        import("src/admin/accounting-export/form/accounting-export.route.tsx"),
    },
  ],
};

const balanceListRoute = {
  lazy: () => import("src/admin/balance-list/BalanceList.layout.tsx"),
  path: routes.admin.balanceList.root,
  children: [
    {
      index: true,
      lazy: () => import("src/admin/balance-list/BalanceList.route.tsx"),
    },
  ],
};

export const buildRoutes = ({ config }: { config: ClientConfig }) => {
  if (config.appVariation === AppVariation.B2bGateDemo) {
    return getB2bGateDemoRoutes();
  }
  if (config.appVariation === AppVariation.B2bGate) {
    return getB2bGateRoutes(config);
  }
  if (config.appVariation === AppVariation.B2bGateSupplier) {
    return getB2bGateSupplierRoutes(config);
  }
  if (config.appVariation === AppVariation.Erp) {
    return getErpRoutes(config);
  }

  throw new Error(`Routes for [${config.appVariation}] are not defined`);
};

const getErpRoutes = (config: ClientConfig) => {
  const modules = getModules(config);
  const erpAdministration = {
    path: routes.admin.administration.root,
    lazy: () => import("src/admin/administration/administration.tsx"),
    children: [basicConfigurationRoute, systemTabRoute, debugRoute],
  };
  return [
    {
      path: routes.admin.root,
      lazy: () => import("src/admin/layout/admin.layout.tsx"),
      children: [
        dashboardRoute,
        erpAdministration,
        billRoute,
        importProductsRoute,
        accountingExportRoute,
        balanceListRoute,
        ...modules.flatMap((module) =>
          getModuleRouteObjects({ module, clientConfig: config }),
        ),
        notFoundRoute,
      ],
    },
    loginPageRoute,
    {
      path: "*",
      loader: () => {
        return redirect(routes.admin.root);
      },
    },
  ];
};

const getB2bGateSupplierRoutes = (config: ClientConfig) => {
  const modules = getModules(config);
  const erpAdministration = {
    path: routes.admin.administration.root,
    lazy: () => import("src/admin/administration/administration.tsx"),
    children: [basicConfigurationRoute],
  };
  return [
    {
      path: routes.admin.root,
      lazy: () => import("src/admin/layout/admin.layout.tsx"),
      children: [
        {
          index: true,
          lazy: () =>
            import(
              "src/admin/layout/B2BGateEmptyScreen/B2BGateEmptyScreen.tsx"
            ),
        },
        ...modules.flatMap((module) =>
          getModuleRouteObjects({ module, clientConfig: config }),
        ),
        erpAdministration,
        importProductsRoute,
        notFoundRoute,
      ],
    },
    loginPageB2BRoute,
    {
      path: "*",
      loader: () => {
        return redirect(routes.admin.root);
      },
    },
  ];
};

const getB2bGateRoutes = (config: ClientConfig) => {
  const modules = getModules(config);
  return [
    {
      path: routes.admin.root,
      lazy: () => import("src/admin/layout/admin.layout.tsx"),
      children: [
        billRoute,
        ...modules.flatMap((module) =>
          getModuleRouteObjects({ module, clientConfig: config }),
        ),
        notFoundRoute,
      ],
    },
    loginPageB2BRoute,
    {
      path: "*",
      loader: () => {
        return redirect(routes.admin.root);
      },
    },
  ];
};

const getB2bGateDemoRoutes = () => {
  return [
    {
      path: "/",
      lazy: () => import("src/auth/pages/LoginPageB2B/components/DemoShop.tsx"),
    },
    {
      path: "*",
      element: <Navigate to={"/"} />,
    },
  ];
};
